'use client';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Clock, Loader2, Pencil, Square, Users } from 'lucide-react';
import { authService } from '@/lib/authService';
import { notify } from '@/lib/notifications';
import { formatAdminAppointmentOverrideSuccessMessage, prepareAdminAppointmentOverride } from '@/lib/work-items/adminAppointmentOverrideClient';
import { normalizeWorkflowStatusKey } from '@/lib/work-items/operationalStatusConfig';
import { getTimePartsSAST, getDateStrSAST } from '@/lib/sastTime';

type UnknownRecord = Record<string, unknown>;

type AppointmentAssigneeCandidate = {
  user_id?: unknown;
  id?: unknown;
  name?: unknown;
  display_name?: unknown;
};

export interface AppointmentAssigneeWorkflowWorkItem {
  id: string;
  status?: string;
  assigned_to?: number | string | null;
  assigned_to_name?: string | null;
  assignee_name?: string | null;
  custom_fields?: UnknownRecord | null;
  assignees?: Array<{
    user_id?: number | string | null;
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
  }>;
}

interface AppointmentAssigneeWorkflowState {
  user_id: number;
  display_name: string;
  workflow_state: string;
  admin_override_status?: string | null;
  state_started_at: string | null;
  active_work_item_id: string | null;
  traveling_to_work_item_id: string | null;
  running_activity_type: string | null;
  first_travel_started_at: string | null;
  last_travel_ended_at: string | null;
  first_work_started_at: string | null;
  last_work_ended_at: string | null;
  total_travel_minutes: number;
  total_work_minutes: number;
}

interface AppointmentAssigneeWorkflowSectionProps {
  workItemId: string;
  workItem?: AppointmentAssigneeWorkflowWorkItem;
  isActive?: boolean;
  readOnly?: boolean;
  onWorkItemPatched?: (updates: { status?: string; custom_fields?: UnknownRecord }) => void;
  userFilterId?: number | null;
  heading?: string;
  description?: string;
  emptyMessage?: string;
}

const APPOINTMENT_ASSIGNEE_OVERRIDE_ACTIONS = [
  { value: 'scheduled', label: 'Reset to scheduled' },
  { value: 'on_route', label: 'Set on route' },
  { value: 'on_site', label: 'Set on site' },
  { value: 'in_progress', label: 'Start in progress' },
  { value: 'paused', label: 'Pause in progress' },
  { value: 'resolved', label: 'End as resolved' },
  { value: 'rescheduled', label: 'Reschedule' },
] as const;

const formatSubtypeLabel = (value: string) => value.replace(/[_-]/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

const normalizeAppointmentAssigneeOverrideStatus = (value: string | null | undefined): string | null => {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (!normalized) {
    return null;
  }

  if (normalized === 'en_route') return 'on_route';
  if (normalized === 'arrived') return 'on_site';
  if (normalized === 'checked_in' || normalized === 'working') return 'in_progress';
  if (normalized === 'on_hold') return 'paused';
  if (normalized === 'reschedule') return 'rescheduled';

  return normalized;
};

const formatDate = (dateString: string | null) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '';

  return date.toLocaleDateString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatMinutesCompact = (minutes?: number | null) => {
  if (!Number.isFinite(minutes) || minutes == null) return '—';

  const rounded = Math.abs(Math.round(minutes));
  const hours = Math.floor(rounded / 60);
  const remainder = rounded % 60;

  if (hours > 0 && remainder > 0) {
    return `${hours}h ${remainder}m`;
  }

  if (hours > 0) {
    return `${hours}h`;
  }

  return `${rounded}m`;
};

export default function AppointmentAssigneeWorkflowSection({
  workItemId,
  workItem,
  isActive = true,
  readOnly = false,
  onWorkItemPatched,
  userFilterId = null,
  heading = 'Assignees',
  description = 'Override each assignee individually. The timing markers below come from that assignee’s own workflow entries so they line up with billing, labour recovery, and SmartTask.',
  emptyMessage = 'No assignees are currently linked to this appointment.',
}: AppointmentAssigneeWorkflowSectionProps) {
  const [appointmentAssigneeStates, setAppointmentAssigneeStates] = useState<AppointmentAssigneeWorkflowState[]>([]);
  const [loadingAppointmentAssigneeStates, setLoadingAppointmentAssigneeStates] = useState(false);
  const [savingAppointmentAssigneeUserId, setSavingAppointmentAssigneeUserId] = useState<number | null>(null);
  const [stoppingTimerUserId, setStoppingTimerUserId] = useState<number | null>(null);
  const [timeEditUserId, setTimeEditUserId] = useState<number | null>(null);
  const [timeEditValues, setTimeEditValues] = useState<{
    travel_started: string;
    travel_ended: string;
    work_started: string;
    work_ended: string;
  }>({ travel_started: '', travel_ended: '', work_started: '', work_ended: '' });
  const [savingTimeEdit, setSavingTimeEdit] = useState(false);

  const getAuthToken = useCallback(() => authService.getTokens()?.access_token || '', []);

  const mergedCustomFields = useMemo<UnknownRecord>(() => {
    return workItem?.custom_fields && typeof workItem.custom_fields === 'object'
      ? workItem.custom_fields
      : {};
  }, [workItem?.custom_fields]);

  const appointmentAssigneeRoster = useMemo(() => {
    const seenUserIds = new Set<number>();
    const roster: Array<{ user_id: number; display_name: string }> = [];

    const pushUser = (userId: unknown, displayName?: unknown) => {
      const numericUserId = typeof userId === 'number'
        ? userId
        : typeof userId === 'string' && userId.trim()
          ? Number(userId)
          : Number.NaN;

      if (!Number.isFinite(numericUserId) || seenUserIds.has(numericUserId)) {
        return;
      }

      seenUserIds.add(numericUserId);
      roster.push({
        user_id: numericUserId,
        display_name: typeof displayName === 'string' && displayName.trim()
          ? displayName.trim()
          : `User ${numericUserId}`,
      });
    };

    (Array.isArray(workItem?.assignees) ? workItem.assignees : []).forEach((assignee) => {
      pushUser(
        assignee.user_id,
        `${assignee.first_name || ''} ${assignee.last_name || ''}`.trim() || assignee.email || undefined,
      );
    });

    pushUser(workItem?.assigned_to, workItem?.assigned_to_name || workItem?.assignee_name || undefined);
    pushUser(userFilterId, workItem?.assigned_to_name || workItem?.assignee_name || undefined);

    const customFieldUserIds = [
      mergedCustomFields.assigned_user_ids,
      mergedCustomFields.participant_user_ids,
      mergedCustomFields.assigned_users,
    ];

    customFieldUserIds.forEach((value) => {
      if (Array.isArray(value)) {
        value.forEach((entry) => {
          if (typeof entry === 'object' && entry) {
            const candidate = entry as AppointmentAssigneeCandidate;
            pushUser(candidate.user_id ?? candidate.id, candidate.name ?? candidate.display_name);
          } else {
            pushUser(entry);
          }
        });
      }
    });

    appointmentAssigneeStates.forEach((assigneeState) => {
      pushUser(assigneeState.user_id, assigneeState.display_name);
    });

    return roster;
  }, [appointmentAssigneeStates, mergedCustomFields.assigned_user_ids, mergedCustomFields.assigned_users, mergedCustomFields.participant_user_ids, workItem?.assigned_to, workItem?.assigned_to_name, workItem?.assignee_name, workItem?.assignees, userFilterId]);

  const showAppointmentAssigneeWorkflowSection = appointmentAssigneeRoster.length > 0;
  const shouldLoadAppointmentAssigneeStates = showAppointmentAssigneeWorkflowSection || (typeof userFilterId === 'number' && Number.isFinite(userFilterId));

  useEffect(() => {
    if (!isActive || !shouldLoadAppointmentAssigneeStates || !workItemId || String(workItemId).startsWith('temp-')) {
      setAppointmentAssigneeStates([]);
      setLoadingAppointmentAssigneeStates(false);
      return;
    }

    let cancelled = false;

    const loadAppointmentAssigneeStates = async () => {
      try {
        setLoadingAppointmentAssigneeStates(true);
        const response = await fetch('/api/v1/work-items/workflow-state', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'get_appointment_assignee_states',
            work_item_id: workItemId,
          }),
        });

        const data = await response.json().catch(() => null);
        if (!cancelled) {
          setAppointmentAssigneeStates(Array.isArray(data?.data?.assignee_states) ? data.data.assignee_states : []);
        }
      } catch (error) {
        console.error('Error loading appointment assignee workflow states:', error);
        if (!cancelled) {
          setAppointmentAssigneeStates([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingAppointmentAssigneeStates(false);
        }
      }
    };

    void loadAppointmentAssigneeStates();

    return () => {
      cancelled = true;
    };
  }, [getAuthToken, isActive, shouldLoadAppointmentAssigneeStates, workItemId]);

  const formatAssigneeWorkflowStatus = useCallback((status: string | null | undefined) => {
    const normalizedStatus = typeof status === 'string' ? status.trim().toLowerCase().replace(/[\s-]+/g, '_') : '';
    const normalizedResolution = typeof mergedCustomFields?.resolution === 'string'
      ? normalizeWorkflowStatusKey(mergedCustomFields.resolution)
      : '';

    switch (normalizedStatus) {
      case 'traveling':
      case 'travel_paused':
        return 'On route';
      case 'arrived':
        return 'On site';
      case 'checked_in':
        return 'In progress';
      case 'paused':
        return 'Paused';
      case 'ready':
        return 'Scheduled';
      case 'idle':
        if (
          normalizeWorkflowStatusKey(workItem?.status) === 'unresolved'
          && normalizedResolution === 'rescheduled'
        ) {
          return 'Rescheduled';
        }
        return formatSubtypeLabel(workItem?.status ?? 'idle');
      default:
        return formatSubtypeLabel(normalizedStatus || 'idle');
    }
  }, [mergedCustomFields?.resolution, workItem?.status]);

  const getAppointmentAssigneeActiveOverrideStatus = useCallback((assignee: AppointmentAssigneeWorkflowState) => {
    const normalizedWorkflowState = normalizeWorkflowStatusKey(assignee.workflow_state);
    const normalizedOverrideStatus = normalizeAppointmentAssigneeOverrideStatus(assignee.admin_override_status);

    if (normalizedWorkflowState === 'checked_in') {
      return 'in_progress';
    }

    if (normalizedWorkflowState === 'paused') {
      return 'paused';
    }

    if (normalizedWorkflowState === 'arrived') {
      return 'on_site';
    }

    if (normalizedWorkflowState === 'traveling' || normalizedWorkflowState === 'travel_paused') {
      return 'on_route';
    }

    if (normalizedWorkflowState === 'ready') {
      return normalizedOverrideStatus === 'confirmed' ? 'confirmed' : 'scheduled';
    }

    if (normalizedWorkflowState === 'idle') {
      return normalizedOverrideStatus;
    }

    return normalizedOverrideStatus;
  }, []);

  const handleAppointmentAssigneeOverride = useCallback(async (assignee: AppointmentAssigneeWorkflowState, requestedStatus: string) => {
    try {
      const preparedOverride = await prepareAdminAppointmentOverride({
        authToken: getAuthToken(),
        workItemId,
        requestedStatus,
        currentStatus: assignee.admin_override_status || assignee.workflow_state,
        subjectLabel: assignee.display_name,
        targetUserId: assignee.user_id,
      });

      if (!preparedOverride) {
        return;
      }

      setSavingAppointmentAssigneeUserId(assignee.user_id);
      const response = await fetch('/api/v1/work-items/workflow-state', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(preparedOverride.requestBody),
      });

      const data = await response.json().catch(() => null);
      if (!response.ok || !data?.success) {
        throw new Error(data?.error || 'Failed to update assignee workflow state');
      }

      setAppointmentAssigneeStates(Array.isArray(data.data?.assignee_states) ? data.data.assignee_states : []);
      onWorkItemPatched?.({
        status: typeof data.data?.status === 'string' ? data.data.status : undefined,
        custom_fields: data.data?.custom_fields && typeof data.data.custom_fields === 'object'
          ? data.data.custom_fields
          : undefined,
      });
      notify.success(formatAdminAppointmentOverrideSuccessMessage({
        requestedStatus,
        successContext: preparedOverride.successContext,
      }));
    } catch (error) {
      console.error('Error overriding appointment assignee workflow state:', error);
      notify.error(error instanceof Error ? error.message : 'Failed to update assignee workflow state');
    } finally {
      setSavingAppointmentAssigneeUserId(null);
    }
  }, [getAuthToken, onWorkItemPatched, workItemId]);

  const handleStopRunningTimer = useCallback(async (assignee: AppointmentAssigneeWorkflowState) => {
    const runningType = assignee.running_activity_type;
    if (!runningType) return;

    const label = formatSubtypeLabel(runningType);
    const confirmed = await notify.confirm({
      title: `Stop ${label} timer`,
      message: `Stop the running ${label.toLowerCase()} timer for ${assignee.display_name}? This will set the end time to now and sync over WebSocket.`,
      confirmText: `Stop ${label}`,
      cancelText: 'Cancel',
      variant: 'warning',
      onConfirm: () => {},
    });

    if (!confirmed) return;

    try {
      setStoppingTimerUserId(assignee.user_id);
      const response = await fetch('/api/v1/work-items/workflow-state', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'admin_stop_running_timer',
          work_item_id: workItemId,
          metadata: {
            target_user_id: assignee.user_id,
            activity_type: runningType,
            ended_at: new Date().toISOString(),
          },
        }),
      });

      const data = await response.json().catch(() => null);
      if (!response.ok || !data?.success) {
        throw new Error(data?.error || 'Failed to stop timer');
      }

      setAppointmentAssigneeStates(Array.isArray(data.data?.assignee_states) ? data.data.assignee_states : []);
      onWorkItemPatched?.({
        status: typeof data.data?.status === 'string' ? data.data.status : undefined,
        custom_fields: data.data?.custom_fields && typeof data.data.custom_fields === 'object'
          ? data.data.custom_fields
          : undefined,
      });
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('workItemTimeEntriesChanged', {
          detail: { workItemId },
        }));
      }
      notify.success(`${label} timer stopped for ${assignee.display_name}`);
    } catch (error) {
      console.error('Error stopping timer:', error);
      notify.error(error instanceof Error ? error.message : 'Failed to stop timer');
    } finally {
      setStoppingTimerUserId(null);
    }
  }, [getAuthToken, onWorkItemPatched, workItemId]);

  const openTimeEditor = useCallback((assignee: AppointmentAssigneeWorkflowState) => {
    const extractTimeForInput = (isoDate: string | null): string => {
      if (!isoDate) return '';
      const d = new Date(isoDate);
      if (Number.isNaN(d.getTime())) return '';
      const parts = getTimePartsSAST(d);
      return `${String(parts.hours).padStart(2, '0')}:${String(parts.minutes).padStart(2, '0')}`;
    };

    setTimeEditUserId(assignee.user_id);
    setTimeEditValues({
      travel_started: extractTimeForInput(assignee.first_travel_started_at),
      travel_ended: extractTimeForInput(assignee.last_travel_ended_at),
      work_started: extractTimeForInput(assignee.first_work_started_at),
      work_ended: extractTimeForInput(assignee.last_work_ended_at),
    });
  }, []);

  const handleSaveTimeEdits = useCallback(async (assignee: AppointmentAssigneeWorkflowState) => {
    const dateStr = getDateStrSAST(new Date());
    const buildIso = (timeVal: string) => timeVal ? `${dateStr}T${timeVal}:00+02:00` : null;

    try {
      setSavingTimeEdit(true);
      const response = await fetch('/api/v1/work-items/workflow-state', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'admin_update_assignee_times',
          work_item_id: workItemId,
          metadata: {
            target_user_id: assignee.user_id,
            first_travel_started_at: buildIso(timeEditValues.travel_started),
            last_travel_ended_at: buildIso(timeEditValues.travel_ended),
            first_work_started_at: buildIso(timeEditValues.work_started),
            last_work_ended_at: buildIso(timeEditValues.work_ended),
          },
        }),
      });

      const data = await response.json().catch(() => null);
      if (!response.ok || !data?.success) {
        throw new Error(data?.error || 'Failed to update assignee times');
      }

      setAppointmentAssigneeStates(Array.isArray(data.data?.assignee_states) ? data.data.assignee_states : []);
      onWorkItemPatched?.({
        status: typeof data.data?.status === 'string' ? data.data.status : undefined,
        custom_fields: data.data?.custom_fields && typeof data.data.custom_fields === 'object'
          ? data.data.custom_fields
          : undefined,
      });
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('workItemTimeEntriesChanged', {
          detail: { workItemId },
        }));
      }
      setTimeEditUserId(null);
      notify.success(`Times updated for ${assignee.display_name}`);
    } catch (error) {
      console.error('Error saving assignee time edits:', error);
      notify.error(error instanceof Error ? error.message : 'Failed to update times');
    } finally {
      setSavingTimeEdit(false);
    }
  }, [getAuthToken, onWorkItemPatched, timeEditValues, workItemId]);

  const displayedAssignees = appointmentAssigneeStates.length > 0
    ? appointmentAssigneeStates
    : appointmentAssigneeRoster.map((assignee) => ({
        ...assignee,
        workflow_state: 'idle',
        admin_override_status: null,
        state_started_at: null,
        active_work_item_id: null,
        traveling_to_work_item_id: null,
        running_activity_type: null,
        first_travel_started_at: null,
        last_travel_ended_at: null,
        first_work_started_at: null,
        last_work_ended_at: null,
        total_travel_minutes: 0,
        total_work_minutes: 0,
      }));

  const filteredAssignees = typeof userFilterId === 'number' && Number.isFinite(userFilterId)
    ? displayedAssignees.filter((assignee) => assignee.user_id === userFilterId)
    : displayedAssignees;

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500">
            Appointment team
          </div>
          <h4 className="mt-1 flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-white">
            <Users className="h-4 w-4 text-blue-500" />
            {heading}
          </h4>
          <p className="mt-1 max-w-3xl text-[13px] text-gray-600 dark:text-gray-400">
            {description}
          </p>
        </div>
        {loadingAppointmentAssigneeStates && (
          <div className="inline-flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Loading assignees...
          </div>
        )}
      </div>

      {filteredAssignees.length > 0 ? (
        <div className="space-y-2">
          {filteredAssignees.map((assignee) => {
            const currentStatusLabel = formatAssigneeWorkflowStatus(assignee.workflow_state);
            const activeOverrideStatus = getAppointmentAssigneeActiveOverrideStatus(assignee);
            const isSavingAssignee = savingAppointmentAssigneeUserId === assignee.user_id;

            return (
              <div
                key={assignee.user_id}
                className="rounded-xl border border-gray-200 bg-gray-50/80 p-3 dark:border-gray-700 dark:bg-gray-900/40"
              >
                <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        {assignee.display_name}
                      </span>
                      <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-200">
                        {currentStatusLabel}
                      </span>
                      {assignee.running_activity_type && (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 pl-2 pr-1 py-0.5 text-[11px] font-medium text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200">
                          <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                          </span>
                          Running: {formatSubtypeLabel(assignee.running_activity_type)}
                          {!readOnly && (
                            <button
                              type="button"
                              onClick={() => { void handleStopRunningTimer(assignee); }}
                              disabled={stoppingTimerUserId === assignee.user_id}
                              className="ml-0.5 inline-flex items-center gap-0.5 rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-bold text-red-700 transition-colors hover:bg-red-200 dark:bg-red-900/50 dark:text-red-300 dark:hover:bg-red-800/60 disabled:opacity-50"
                              title="Stop this running timer now"
                            >
                              <Square size={8} />
                              {stoppingTimerUserId === assignee.user_id ? '…' : 'Stop'}
                            </button>
                          )}
                        </span>
                      )}
                      {!readOnly && (
                        <button
                          type="button"
                          onClick={() => timeEditUserId === assignee.user_id ? setTimeEditUserId(null) : openTimeEditor(assignee)}
                          className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-600 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700"
                          title="Edit timestamps"
                        >
                          <Pencil size={9} />
                          {timeEditUserId === assignee.user_id ? 'Close' : 'Edit times'}
                        </button>
                      )}
                    </div>

                    <div className="mt-2 grid grid-cols-2 gap-2 xl:grid-cols-4 text-[11px] text-gray-600 dark:text-gray-400">
                      <div className="rounded-lg bg-white px-2.5 py-2 dark:bg-gray-800">
                        <div className="font-medium">Travel started</div>
                        <div className="mt-0.5">{formatDate(assignee.first_travel_started_at) || '—'}</div>
                      </div>
                      <div className="rounded-lg bg-white px-2.5 py-2 dark:bg-gray-800">
                        <div className="font-medium">Travel ended</div>
                        <div className="mt-0.5">{formatDate(assignee.last_travel_ended_at) || '—'}</div>
                      </div>
                      <div className="rounded-lg bg-white px-2.5 py-2 dark:bg-gray-800">
                        <div className="font-medium">Work started</div>
                        <div className="mt-0.5">{formatDate(assignee.first_work_started_at) || '—'}</div>
                      </div>
                      <div className="rounded-lg bg-white px-2.5 py-2 dark:bg-gray-800">
                        <div className="font-medium">Work ended</div>
                        <div className="mt-0.5">{formatDate(assignee.last_work_ended_at) || '—'}</div>
                      </div>
                    </div>

                    {timeEditUserId === assignee.user_id && (
                      <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50/50 p-3 dark:border-amber-800/60 dark:bg-amber-950/20">
                        <div className="flex items-center gap-2 mb-2">
                          <Clock size={12} className="text-amber-600 dark:text-amber-400" />
                          <span className="text-[11px] font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-300">
                            Edit assignee timestamps (SAST)
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 xl:grid-cols-4">
                          <div>
                            <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-500 mb-0.5">Travel started</label>
                            <input
                              type="time"
                              value={timeEditValues.travel_started}
                              onChange={(e) => setTimeEditValues((prev) => ({ ...prev, travel_started: e.target.value }))}
                              className="w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-900 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-500 mb-0.5">Travel ended</label>
                            <input
                              type="time"
                              value={timeEditValues.travel_ended}
                              onChange={(e) => setTimeEditValues((prev) => ({ ...prev, travel_ended: e.target.value }))}
                              className="w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-900 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-500 mb-0.5">Work started</label>
                            <input
                              type="time"
                              value={timeEditValues.work_started}
                              onChange={(e) => setTimeEditValues((prev) => ({ ...prev, work_started: e.target.value }))}
                              className="w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-900 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-500 mb-0.5">Work ended</label>
                            <input
                              type="time"
                              value={timeEditValues.work_ended}
                              onChange={(e) => setTimeEditValues((prev) => ({ ...prev, work_ended: e.target.value }))}
                              className="w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-900 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                            />
                          </div>
                        </div>
                        <div className="mt-2 flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => { void handleSaveTimeEdits(assignee); }}
                            disabled={savingTimeEdit}
                            className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-[11px] font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
                          >
                            {savingTimeEdit ? 'Saving…' : 'Save time changes'}
                          </button>
                          <button
                            type="button"
                            onClick={() => setTimeEditUserId(null)}
                            className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-[11px] font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                          >
                            Cancel
                          </button>
                          <span className="text-[10px] text-gray-400 dark:text-gray-500">
                            Changes sync to SmartTask via WebSocket
                          </span>
                        </div>
                      </div>
                    )}

                    <div className="mt-2 flex flex-wrap gap-1.5 text-[11px] text-gray-600 dark:text-gray-300">
                      <span className="inline-flex items-center rounded-full bg-white px-2 py-1 dark:bg-gray-800 dark:text-gray-300">
                        Travel total: {formatMinutesCompact(assignee.total_travel_minutes)}
                      </span>
                      <span className="inline-flex items-center rounded-full bg-white px-2 py-1 dark:bg-gray-800 dark:text-gray-300">
                        Work total: {formatMinutesCompact(assignee.total_work_minutes)}
                      </span>
                      <span className="inline-flex items-center rounded-full bg-white px-2 py-1 dark:bg-gray-800 dark:text-gray-300">
                        State since: {formatDate(assignee.state_started_at) || '—'}
                      </span>
                    </div>
                  </div>

                  <div className="xl:w-[15rem] xl:flex-shrink-0">
                    <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                      Override status
                    </div>
                    <div className="grid grid-cols-2 gap-1.5">
                      {APPOINTMENT_ASSIGNEE_OVERRIDE_ACTIONS.map((action) => {
                        const isActionActive = activeOverrideStatus === action.value;

                        return (
                          <button
                            key={`${assignee.user_id}-${action.value}`}
                            type="button"
                            onClick={() => { void handleAppointmentAssigneeOverride(assignee, action.value); }}
                            disabled={readOnly || Boolean(isSavingAssignee)}
                            className={`rounded-lg border px-2 py-1.5 text-[11px] font-medium transition-colors ${isActionActive
                              ? 'border-green-600 bg-green-600 text-white shadow-sm hover:bg-green-700'
                              : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800 dark:disabled:bg-gray-800 dark:disabled:text-gray-500'
                            }`}
                          >
                            {isSavingAssignee ? 'Saving…' : action.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-gray-300 px-4 py-5 text-sm text-gray-600 dark:border-gray-700 dark:text-gray-400">
          {emptyMessage}
        </div>
      )}
    </div>
  );
}
